import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Routes where a "Back" button doesn't make sense — entry points / dead-ends.
const HIDE_BACK_ON = new Set(['/', '/login', '/register', '/permissions']);

/**
 * Floating back button rendered globally by App.js.
 * - Hides on splash, home, login and register routes.
 * - Calls navigate(-1) when the browser history has at least one prior entry,
 *   otherwise falls back to "/" so the user is never trapped.
 * - Direction-aware: the chevron flips for RTL.
 */
export const BackButton = () => {
    const { t, language } = useLanguage();
    const location = useLocation();
    const navigate = useNavigate();
    const isRTL = language === 'ar';

    if (HIDE_BACK_ON.has(location.pathname)) return null;

    // Patient profile: the logo replaces the back arrow on phones (the page is
    // a tab-bar destination there, so "back" has no meaning).
    if (location.pathname === '/patient') {
        return (
            <button
                type="button"
                onClick={() => navigate('/')}
                aria-label="Tababa.co"
                data-testid="patient-profile-logo-button"
                className={`md:hidden fixed top-3 z-40 w-11 h-11 rounded-full bg-white/95 shadow-md ring-1 ring-black/5
                    backdrop-blur flex items-center justify-center overflow-hidden
                    ${isRTL ? 'right-3' : 'left-3'}`}
            >
                <img src="/icons/icon-192.png" alt="Tababa.co" className="w-9 h-9 rounded-full object-cover" />
            </button>
        );
    }

    const handleClick = () => {
        // window.history.length === 1 means this tab was opened directly on this URL;
        // a regular navigate(-1) would just stay put, so we send the user home instead.
        if (typeof window !== 'undefined' && window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/');
        }
    };

    const Chevron = isRTL ? ChevronRight : ChevronLeft;

    return (
        <button
            type="button"
            onClick={handleClick}
            aria-label={t('رجوع', 'Back', 'Retour')}
            data-testid="global-back-button"
            className={`fixed top-3 z-40 flex items-center gap-1.5 px-3 h-10
                rounded-full bg-white/90 text-[#0d47a1] shadow-md ring-1 ring-black/5
                backdrop-blur hover:bg-white hover:shadow-lg
                ${isRTL ? 'right-3' : 'left-3'}`}
        >
            <Chevron size={18} strokeWidth={2.4} />
            <span className="text-sm font-semibold hidden sm:inline">
                {t('رجوع', 'Back', 'Retour')}
            </span>
        </button>
    );
};

export default BackButton;
